/**
 * CertificationPathCard — one-glance Beta training progression.
 *
 * Shows the officer's current training tier, next target, and the difficulty
 * that rides into each session.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { initFirebaseClient } from '@copapp/firebase/client';
import { useAuth } from '../../contexts/AuthContext';
import { COLLECTIONS, SUBCOLLECTIONS } from '@copapp/core/constants';
import { certPath, type CertTier } from '@copapp/core';

const DIFFICULTIES = ['easy', 'moderate', 'hard', 'expert'] as const;

export const TIER_TEXT_COLORS: Record<CertTier['key'], string> = {
  bronze: 'text-amber-600',
  silver: 'text-fg-muted',
  gold: 'text-amber-400',
  platinum: 'text-fg',
  diamond: 'text-cyan-300',
};

interface DemoCert {
  avg: number;
  sessionCount: number;
  difficulty: string;
  rolling: {
    tierKey: CertTier['key'];
    tierName: string;
    windowAvg: number | null;
    window: number;
    next: { name: string; threshold: number; window: number } | null;
  };
  /** Where "Take the Next Call" goes in the demo (replays the ride-along). */
  onNextCall: () => void;
}

interface Props {
  /**
   * DEMO MODE (docs/DEMO.md): sample standing for the public /demo
   * ride-along. No listeners attach; difficulty chips stay local.
   */
  demo?: DemoCert;
}

export function CertificationPathCard({ demo }: Props = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [avg, setAvg] = useState<number | null>(demo?.avg ?? null);
  const [sessionCount, setSessionCount] = useState(demo?.sessionCount ?? 0);
  const [difficulty, setDifficulty] = useState<string>(demo?.difficulty ?? 'moderate');
  const [rolling, setRolling] = useState<{
    tierKey: CertTier['key'];
    tierName: string;
    windowAvg: number | null;
    window: number;
    next: { name: string; threshold: number; window: number } | null;
  } | null>(demo?.rolling ?? null);

  // The rolling training average — server-refreshed after every insight.
  useEffect(() => {
    if (demo) return;
    if (!user?.uid) return;
    const { db } = initFirebaseClient();
    const ref = doc(db, COLLECTIONS.OFFICERS, user.uid, SUBCOLLECTIONS.INSIGHTS_AGGREGATED, 'latest');
    return onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const trends = data.pattern_analysis?.trends || {};
      const values = Object.values(trends).map((s: unknown) => (s as { avg: number }).avg);
      setAvg(values.length ? values.reduce((a: number, b: number) => a + b, 0) / values.length : null);
      setSessionCount(data.pattern_analysis?.session_count || 0);
      // Belt semantics from the server (W10): tier judged over its window.
      setRolling(data.certification ?? null);
    }, (err) => console.warn('cert-path listener error:', err.code));
  }, [user?.uid, demo]);

  // The officer's chosen difficulty rides their profile.
  useEffect(() => {
    if (demo) return;
    if (!user?.uid) return;
    const { db } = initFirebaseClient();
    return onSnapshot(doc(db, COLLECTIONS.OFFICERS, user.uid), (snap) => {
      const pref = snap.data()?.preferredDifficulty;
      if (typeof pref === 'string' && (DIFFICULTIES as readonly string[]).includes(pref)) {
        setDifficulty(pref);
      }
    }, (err) => console.warn('difficulty listener error:', err.code));
  }, [user?.uid, demo]);

  const chooseDifficulty = (d: string) => {
    setDifficulty(d);
    if (demo || !user?.uid) return;
    const { db } = initFirebaseClient();
    void setDoc(doc(db, COLLECTIONS.OFFICERS, user.uid), { preferredDifficulty: d }, { merge: true })
      .catch(() => { /* chip state is instant; profile write is best-effort */ });
  };

  // Server-computed rolling certification wins; legacy path is the fallback
  // for aggregates written before the belt-semantics block existed.
  const path = certPath(avg ?? 0);
  const tierName = rolling?.tierName ?? path.tier.name;
  const tierKey = rolling?.tierKey ?? path.tier.key;
  const shownAvg = rolling?.windowAvg ?? avg;
  const nextRung = rolling
    ? rolling.next
    : path.next
      ? { name: path.next.name, threshold: path.next.threshold, window: null as number | null }
      : null;
  const hasTrainingTier = shownAvg !== null && sessionCount >= 3;
  const tierColor = hasTrainingTier ? TIER_TEXT_COLORS[tierKey] : 'text-fg';

  return (
    <div
      data-testid="cert-path-card"
      className="mb-6 bg-copapp-navy-card border border-copapp-line rounded-lg p-4"
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[10px] text-copapp-400 uppercase tracking-wider font-semibold">
              ◈ CopApp Training Tier · your standing
            </p>
            <span className="rounded-full border border-copapp-500/40 bg-copapp-600/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-copapp-300">
              Beta
            </span>
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className={`text-base font-bold ${tierColor}`}>
              {hasTrainingTier ? tierName : 'Baseline'}
            </span>
            <span className="text-xs text-fg-faint">
              {hasTrainingTier
                ? `${shownAvg.toFixed(1)}/10${rolling ? ` over last ${Math.min(rolling.window, sessionCount)}` : ' avg'} · ${sessionCount} sessions`
                : `${sessionCount} of 3 scored calls`}
            </span>
          </div>
          <p className="text-sm text-fg-muted mt-1.5">
            {!hasTrainingTier
              ? 'Complete three scored calls to establish a beta training tier.'
              : nextRung
              ? <>Next tier target: <span className="font-semibold text-fg">{nextRung.threshold.toFixed(1)}/10</span>{nextRung.window ? ` over your last ${nextRung.window}` : ''} → {nextRung.name}</>
              : 'Highest training tier — keep practicing.'}
          </p>
          <p className="text-[10px] text-fg-faint mt-1.5">
            Beta training progress only — not a professional or public credential.
          </p>

          {/* Chosen difficulty — persists to the profile, rides every session */}
          <div
            role="group"
            aria-label="Preferred training difficulty"
            className="flex flex-wrap items-center gap-1.5 mt-2.5"
          >
            <span className="text-[10px] text-fg-faint uppercase tracking-wider mr-1">Difficulty</span>
            {DIFFICULTIES.map((d) => (
              <button
                type="button"
                key={d}
                onClick={() => chooseDifficulty(d)}
                aria-pressed={difficulty === d}
                className={`text-[10px] px-2 py-0.5 rounded-full capitalize transition-colors ${
                  difficulty === d
                    ? 'bg-copapp-700 text-white'
                    : 'bg-copapp-navy-abyss text-fg-muted border border-copapp-line hover:border-copapp-line-bright'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => (demo ? demo.onNextCall() : navigate('/sim'))}
          className="w-full sm:w-auto px-4 py-2 bg-copapp-navy-abyss border border-copapp-line hover:border-copapp-line-bright rounded-lg text-sm font-semibold text-fg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copapp-400"
        >
          Take the Next Call
        </button>
      </div>
    </div>
  );
}
